<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Popover from '$lib/components/Popover/Popover.svelte';
	import Icon from '$lib/components/Icon/Icon.svelte';
	import ConfirmModal from '$lib/components/Modal/ConfirmModal.svelte';
	import { getHubStores } from '$lib/stores/hubStores';
	import Modal from '$lib/components/Modal/Modal.svelte';
	import Size from '$lib/components/Modal/size';
	import PerformancePage from '$lib/components/headerBar/performance/PerformancePage.svelte';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import { goto } from '$app/navigation';
	import { page } from '$app/stores';
	import type {
		OptionsMenuPerms,
		PostHeaderParent,
	} from '$lib/components/Post/OptionsMenu/getOptionsMenuPerms';
	import { getPostContextStore } from '$lib/components/Post/postContextStore';
	import {
		toggleFeature,
		toggleHide,
		togglePin,
		openSettingsModal,
	} from '$lib/components/Post/postCommands';
	import type { PostStatus } from '$lib/api/restApi';
	import { editPost } from '$textEditor/PostComposer/composerStore';
	import { editWidget, isWidget } from '$lib/components/WidgetModal/widgetHelpers';
	import type { CommentFilter } from '$lib/api/rpc';

	const post = getPostContextStore();

	const origin = `${$page.url.origin}/${$page.params.hubPrefix}`;

	const { hub, posts, featuredPosts, pinnedPosts, restApi, api } = getHubStores();
	export let postIndex: number | undefined = undefined;
	export let parent: PostHeaderParent;
	export let permissions: OptionsMenuPerms;

	$: deleting = false;

	$: pinned = $pinnedPosts.some((pinned) => pinned.id == $post.id);
	$: hidden = ($post.status as PostStatus) == 'hidden';
	$: featured = $featuredPosts.some((featured) => featured.id == $post.id);

	const handlePin = async () => {
		await togglePin(postIndex, restApi, pinnedPosts, post, pinned);
	};

	const handleFeature = async () => {
		try {
			await toggleFeature(api, featuredPosts, post);
		} catch (e) {
			toastMessages.danger('', e);
		}
	};

	const handleHide = async () => {
		await toggleHide(
			restApi,
			posts,
			post,
			// We only update the pinned posts store if the current post is pinned
			pinned ? pinnedPosts : undefined,
			// We only update the featured posts store if the current post is featured
			featured ? featuredPosts : undefined
		);
	};

	const handleDelete = async () => {
		await restApi.deletePost($hub.id, $post.id, $post.status);
		const postStatusToUrlSegment: {
			[key: string]: string;
		} = {
			published: 'published',
			scheduled: 'scheduled',
			draft: 'drafts',
			'in-review': 'submissions',
		};

		switch (parent) {
			case 'manageContentPage':
				goto(`${origin}/content/${postStatusToUrlSegment[$post.status]}`);
				break;
			case 'postDetailPage':
				await goto('../');
				break;
			default:
				posts.update((old) => old.filter((p) => p.id !== $post.id));

				if (pinned) {
					pinnedPosts.update((old) => old.filter((p) => p.id !== $post.id));
				}

				if (featured) {
					featuredPosts.update((old) => old.filter((p) => p.id !== $post.id));
				}
		}
	};

	let performancePageModalIsOpened = false;
	const editHandler = async () => {
		try {
			if (isWidget($post.type)) {
				editWidget(post);
			} else {
				await editPost(post, api);
			}
		} catch (e) {
			toastMessages.danger('', e);
		}
	};

	const exportComments = async () => {
		const filter: CommentFilter = {
			postIDs: [$post.id],
			created_at_range: {
				start: undefined,
				end: undefined,
			},
		};
		const resp = await restApi.exportAllComments($hub.id, filter);

		if (resp.status !== 200) {
			toastMessages.danger($_('common.error.wrong'));
		}

		let hiddenElement = document.createElement('a');
		var csvData = new Blob([resp.data], { type: 'text/csv;charset=utf8' });
		var csvUrl = URL.createObjectURL(csvData);
		hiddenElement.href = csvUrl;
		hiddenElement.download = `${$_('hubs.management.comments')}_${new Date().toLocaleDateString()}`;
		hiddenElement.click();
	};
</script>

<Modal
	onClose={() => {
		performancePageModalIsOpened = false;
	}}
	isOpen={performancePageModalIsOpened}
	type={Size.Large}
>
	<div slot="content"><PerformancePage post={$post} /></div></Modal
>

<Popover closeOnClick={true} position="bottom-inline-start">
	<Icon icon="options" size="24" color="gray" slot="target" />

	<ul slot="content">
		{#if permissions.edit}
			<li>
				<button type="button" on:click={editHandler}>
					<Icon icon="compose" size="24" color="rgb(76, 83, 93)" /><span>{$_('common.edit')}</span>
				</button>
			</li>
		{/if}
		{#if permissions.settings && !hidden}
			<li>
				<button type="button" on:click={() => openSettingsModal(api, post)} class="menuItem">
					<Icon icon="settings" size={'24'} color="rgb(76, 83, 93)" />
					<span>{$_('post.menu.settings')}</span>
				</button>
			</li>
		{/if}
		{#if permissions.performance && !hidden}
			<li>
				<button
					type="button"
					on:click={() => {
						performancePageModalIsOpened = true;
					}}
					class="menuItem"
				>
					<Icon icon="analytics" size={'24'} color="rgb(76, 83, 93)" /><span
						>{$_('post.menu.performance')}</span
					>
				</button>
			</li>
		{/if}
		{#if permissions.feature && !hidden}
			<li>
				<button type="button" class="menuItem" on:click={handleFeature}>
					<Icon icon="feature" size={'24'} color={featured ? '#007bb6' : 'rgb(76, 83, 93)'} /><span
						>{featured ? $_('post.menu.unfeature') : $_('post.menu.feature')}</span
					>
				</button>
			</li>
		{/if}
		{#if permissions.pin && !hidden}
			<li>
				<button type="button" class="menuItem" on:click={handlePin}>
					<Icon icon="pin" size={'24'} color={pinned ? '#007bb6' : 'rgb(76, 83, 93)'} /><span
						>{pinned ? $_('post.menu.unpin') : $_('post.menu.pin')}</span
					>
				</button>
			</li>
		{/if}
		{#if permissions.hide}
			<li>
				<button type="button" class="menuItem" on:click={handleHide}>
					<Icon
						icon="hide-content"
						size={'24'}
						color={hidden ? '#007bb6' : 'rgb(76, 83, 93)'}
					/><span>{hidden ? $_('post.menu.unhide') : $_('post.menu.hide')}</span>
				</button>
			</li>
		{/if}

		{#if permissions.exportImages && !hidden}
			<li>
				<a
					href={`${$page.data.apiUrl}/hubs/${$hub.id}/posts/${$post.id}/export/images`}
					class="exportLink {$post.contribution_has_image ? '' : 'disabled'}"
					download
				>
					<button type="button" class="menuItem" disabled={!$post.contribution_has_image}>
						<Icon icon="add-image" size={'24'} color="rgb(76, 83, 93)" /><span
							>{$_('post.menu.export.images')}</span
						>
					</button>
				</a>
			</li>
		{/if}
		{#if permissions.exportComments && !hidden}
			<li>
				<button
					type="button"
					class="menuItem"
					disabled={$post.comment_count === 0}
					on:click={exportComments}
				>
					<Icon icon="download" size={'24'} color="rgb(76, 83, 93)" /><span
						>{$_('post.menu.export.comments')}</span
					>
				</button>
			</li>
		{/if}
		{#if permissions.delete}
			<li>
				<button
					type="button"
					class="menuItem"
					on:click={() => {
						deleting = true;
					}}
				>
					<Icon icon="delete" size={'24'} color="rgb(76, 83, 93)" /><span
						>{$_('post.menu.delete')}</span
					>
				</button>
			</li>
		{/if}
	</ul>
</Popover>
{#if deleting}
	<ConfirmModal
		title={$_('post.options.delete.title', { values: { post: 'Post' } })}
		content={$_('post.confirmation.delete', { values: { type: 'post' } })}
		submitText={$_('post.options.delete.confirm.text', { values: { post: 'Post' } })}
		onCancel={() => {
			deleting = false;
		}}
		onSubmit={handleDelete}
		isOpen={true}
	/>
{/if}

<style>
	li {
		border-block-end: 1px solid var(--grayLighter);
		cursor: pointer;
	}

	li span {
		margin-block: 0 !important;
		margin-inline: var(--gutterSmall) !important;
		color: var(--grayDark);
	}

	li:last-child {
		border-block-end: none;
	}

	button {
		all: unset;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		inline-size: 200px;
		padding-block: var(--gutterSmall);
		padding-inline: var(--gutterMedium);
		color: var(--grayDark);
		font-size: var(--fontSizeSmall);
		text-decoration: none;
	}

	button:hover {
		background-color: var(--grayLighter);
	}

	button:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.exportLink {
		text-decoration: none;
	}

	.exportLink.disabled {
		pointer-events: none;
	}
</style>
